import React from 'react'
import { useMeasure } from 'react-use'

import Button from '../../base/Button'
import Pill from '../../base/Pill'
import ReturnedCallOut from '../../base/ReturnedCallout'
import useTranslation from '../../context/hooks/useTranslation'
import { useAppDispatch } from '../../redux/hooks'
import { setReturnAcknowledgementModal } from '../../redux/MorningScan'
import { useGetMorningScanResultQuery } from '../../redux/services/morning-scan'
import { useGetReturnShipmentRequestsQuery } from '../../redux/services/returns'
import { BatchHubOutParams, BatchHubOutPayload } from '../../types/BatchHubOut'
import { MorningScanShipment } from '../../types/MorningScanResponse'
import ReturnAcknowledgementDialog from '../ReturnAcknowledgementDialog'
import ScanLoadingModal from '../ScanLoadingModal'
import ShipmentDetailsDialog from '../ShipmentDetailsDialog'
import ShipmentTable from '../ShipmentTable'

export interface MorningScanResult {
  subcoId: number
  onBatchHubOut: (params: BatchHubOutParams) => Promise<void> | void
  isBatchHubOutDone?: boolean
}

export const MorningScanResult: React.FC<MorningScanResult> = ({
  subcoId,
  onBatchHubOut,
  isBatchHubOutDone,
}) => {
  const { t } = useTranslation()
  const { data } = useGetMorningScanResultQuery(subcoId)
  const dispatch = useAppDispatch()
  const keepOnHubShipmentIds = data?.keepInRegion.map(
    (shipment) => shipment.shipmentId,
  )
  const { data: forReturns } = useGetReturnShipmentRequestsQuery(
    keepOnHubShipmentIds || [],
    {
      skip: !keepOnHubShipmentIds?.length,
    },
  )

  const [ref, { width, height }] = useMeasure<HTMLDivElement>()

  const showAcknowledgeModal = () => {
    dispatch(setReturnAcknowledgementModal(true))
  }

  const tableWidth = width / 2 - 8
  const tableHeight = height - 85

  const handleConfirm = async () => {
    try {
      const shipmentsToReturnToTrunkrs = data?.backToTrunkrs.reduce(
        (acc: BatchHubOutPayload, shipment: MorningScanShipment) => {
          acc.push({ shipmentId: shipment.shipmentId, subcoId })
          return acc
        },
        [],
      )

      if (!shipmentsToReturnToTrunkrs) return

      console.log('lib:', shipmentsToReturnToTrunkrs)

      await onBatchHubOut({
        payload: shipmentsToReturnToTrunkrs,
        inventoryScanCompleted: true,
      })
    } catch {
      throw new Error('BatchHubOut error')
    } finally {
      dispatch(setReturnAcknowledgementModal(false))
    }
  }

  return (
    <>
      <div
        className="flex h-full w-full flex-row justify-evenly gap-4"
        ref={ref}
      >
        <div className="flex w-full flex-col">
          <Pill className="bg-orange-60" count={data?.keepInRegion.length || 0}>
            {t('RETURNS_SCAN.KEEP_IN_REGION')}
          </Pill>

          <ShipmentTable
            width={tableWidth}
            height={tableHeight}
            data={data?.keepInRegion}
            forReturns={forReturns}
          />
        </div>
        <div className="flex w-full flex-col">
          <div className="flex max-h-9 w-full flex-row justify-between">
            <Pill
              className="text-white"
              count={data?.backToTrunkrs.length || 0}
            >
              {t('RETURNS_SCAN.BACK_TO_TRUNKRS')}
            </Pill>

            {isBatchHubOutDone ? (
              <ReturnedCallOut />
            ) : (
              <Button
                onClick={showAcknowledgeModal}
                disabled={!data?.backToTrunkrs.length}
              >
                {t('RETURNS_SCAN.BUTTON_CONFIRM_RETURNS')}
              </Button>
            )}
          </div>

          <ShipmentTable
            width={tableWidth}
            height={tableHeight}
            data={data?.backToTrunkrs}
          />
        </div>
      </div>
      <ShipmentDetailsDialog />
      <ReturnAcknowledgementDialog onConfirm={handleConfirm} />

      <ScanLoadingModal />
    </>
  )
}

export default MorningScanResult
